import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Store } from '../Store';
import { toast } from 'react-toastify';
import {
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Pencil,
  X,
  Check,
  Ticket,
  Calendar,
} from 'lucide-react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import userService from '../services/user.service';
import reservaService from '../services/reserva.service';
import { getError } from '../utils';

function normalizeReservasList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.reservas)) return data.reservas;
  return [];
}

/* ── Eye-toggle password input ─────────────────────────────────────────── */
function EyeInput({ value, onChange, placeholder, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        style={{
          width: '100%',
          backgroundColor: '#F5F5F5',
          border: '1px solid #E8E8E8',
          borderRadius: '8px',
          padding: '12px 44px 12px 14px',
          fontSize: '14px',
          color: '#1A1A1A',
          outline: 'none',
        }}
        autoComplete={autoComplete}
      />
      <button
        type='button'
        onClick={() => setShow(v => !v)}
        style={{
          position: 'absolute',
          right: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#ABABAB',
          padding: 0,
        }}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

/* ── Read-only field row ────────────────────────────────────────────────── */
function ReadField({ label, value }) {
  return (
    <div>
      <p
        style={{
          fontSize: '11px',
          fontWeight: '500',
          color: '#ABABAB',
          marginBottom: '2px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </p>
      <p style={{ fontSize: '15px', color: '#1A1A1A', fontWeight: '500' }}>{value || '—'}</p>
    </div>
  );
}

/* ── Editable text input ─────────────────────────────────────────────────  */
function EditInput({ label, value, onChange, type = 'text', autoComplete }) {
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: '12px',
          fontWeight: '500',
          color: '#7C7C7C',
          marginBottom: '6px',
        }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        style={{
          width: '100%',
          backgroundColor: '#F5F5F5',
          border: '1px solid #E8E8E8',
          borderRadius: '8px',
          padding: '12px 14px',
          fontSize: '14px',
          color: '#1A1A1A',
          outline: 'none',
        }}
      />
    </div>
  );
}

export default function ProfileScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;

  /* ── Info form ─── */
  const [editing, setEditing] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [nombre, setNombre] = useState(userInfo?.nombre || '');
  const [email, setEmail] = useState(userInfo?.email || '');
  const [telefono, setTelefono] = useState(userInfo?.telefono || '');

  /* ── Password card ─── */
  const [pwOpen, setPwOpen] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  /* ── Tabs ─── */
  const [activeTab, setActiveTab] = useState('reservas');

  /* ── Reservas ─── */
  const [reservas, setReservas] = useState([]);
  const [loadingReservas, setLoadingReservas] = useState(false);
  const [reservasError, setReservasError] = useState('');

  useEffect(() => {
    if (!userInfo?.email) return;
    setLoadingReservas(true);
    setReservasError('');
    reservaService
      .getReservasForUser(userInfo.email)
      .then(data => setReservas(normalizeReservasList(data)))
      .catch(err => setReservasError(getError(err)))
      .finally(() => setLoadingReservas(false));
  }, [userInfo?.email]);

  if (!userInfo) {
    navigate('/signin');
    return null;
  }

  const statusColor = estado => {
    switch (estado) {
      case 'Confirmada':
      case 'Verificada':
        return { bg: '#DCFCE7', fg: '#166534' };
      case 'Cancelada':
        return { bg: '#FEE2E2', fg: '#991B1B' };
      case 'Automatizacion':
        return { bg: '#DBEAFE', fg: '#1E40AF' };
      default:
        return { bg: '#FEF3C7', fg: '#92400E' };
    }
  };

  /* ── Avatar initials ─── */
  const initials = (userInfo.nombre || '?')
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

  /* ── Save info ─── */
  const saveInfo = async e => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      const data = await userService.updateProfile(
        userInfo._id,
        { nombre, email, telefono },
        userInfo.token
      );
      ctxDispatch({ type: 'USER_SIGNIN', payload: data });
      localStorage.setItem('userInfo', JSON.stringify(data));
      toast.success(t('profile.updateSuccess'));
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.errorOcurred'));
    }
    setSavingInfo(false);
  };

  const cancelEdit = () => {
    setNombre(userInfo.nombre || '');
    setEmail(userInfo.email || '');
    setTelefono(userInfo.telefono || '');
    setEditing(false);
  };

  /* ── Save password ─── */
  const savePassword = async e => {
    e.preventDefault();
    if (newPass !== confirmPw) {
      toast.error(t('auth.passwordsDontMatch'));
      return;
    }
    setSavingPw(true);
    try {
      await userService.updatePassword(userInfo._id, oldPass, newPass, userInfo.token);
      toast.success(t('profile.passwordChanged', 'Contraseña actualizada'));
      setOldPass('');
      setNewPass('');
      setConfirmPw('');
      setPwOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.errorOcurred'));
    }
    setSavingPw(false);
  };

  return (
    <div className='min-h-screen bg-white font-sans text-ink pb-10'>
      <Helmet>
        <title>{t('profile.personalInfo')}</title>
      </Helmet>

      <div className='mx-auto max-w-md px-4 pt-8 space-y-4'>
        {/* ── Avatar header ─────────────────────────────── */}
        <div className='flex flex-col items-center gap-3 py-4'>
          <div
            className='flex h-16 w-16 items-center justify-center rounded-full text-white text-xl font-bold shadow'
            style={{ backgroundColor: 'var(--primaryColor, #333)' }}
          >
            {initials}
          </div>
          <div className='text-center'>
            <p className='text-base font-semibold text-gray-900'>{userInfo.nombre}</p>
            <p className='text-sm text-gray-500'>{userInfo.email}</p>
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────── */}
        <div className='flex rounded-xl overflow-hidden shadow-sm bg-white border border-gray-100'>
          {[
            { key: 'reservas', label: t('profile.myReservas', 'Mis reservas') },
            { key: 'settings', label: t('profile.settings', 'Configuración') },
          ].map(tab => (
            <button
              key={tab.key}
              type='button'
              onClick={() => setActiveTab(tab.key)}
              className='flex-1 py-3 text-sm font-medium transition-colors'
              style={{
                backgroundColor:
                  activeTab === tab.key ? 'var(--secondaryColor, #333)' : 'transparent',
                color: activeTab === tab.key ? '#fff' : '#7C7C7C',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ══════════ Mis reservas ══════════ */}
        {activeTab === 'reservas' && (
          <div className='space-y-3'>
            {loadingReservas ? (
              <div className='rounded-2xl bg-white shadow-sm px-5 py-8 text-center text-sm text-gray-500'>
                {t('common.loading', 'Cargando…')}
              </div>
            ) : reservasError ? (
              <div className='rounded-2xl bg-white shadow-sm px-5 py-4 text-sm text-red-600'>
                {reservasError}
              </div>
            ) : normalizeReservasList(reservas).length === 0 ? (
              <div className='rounded-2xl bg-white shadow-sm px-5 py-10 flex flex-col items-center gap-3 text-center'>
                <div
                  className='flex h-14 w-14 items-center justify-center rounded-full'
                  style={{ backgroundColor: '#F5F5F5' }}
                >
                  <Ticket size={24} style={{ color: '#ABABAB' }} />
                </div>
                <p className='font-medium text-gray-700'>
                  {t('profile.noReservas', 'Todavía no tenés reservas')}
                </p>
                <p className='text-[13px] text-gray-500'>
                  {t('profile.noReservasHelp', 'Cuando reserves un bono, va a aparecer acá.')}
                </p>
              </div>
            ) : (
              normalizeReservasList(reservas).map(r => {
                const sc = statusColor(r.estado);
                // Bonos with hidden numbers only report the quantity
                const ticketsCount = r.numerosOcultos
                  ? Number(r.cantidadTickets) || 0
                  : Array.isArray(r.tickets)
                    ? r.tickets.length
                    : 0;
                return (
                  <div
                    key={r._id}
                    className='rounded-2xl bg-white shadow-sm overflow-hidden border border-gray-100'
                  >
                    {r.rifa?.rifaImage?.fileLink && (
                      <img
                        src={r.rifa.rifaImage.fileLink}
                        alt={r.rifa.nombre}
                        className='w-full object-cover'
                        style={{ height: '120px' }}
                      />
                    )}
                    <div className='px-5 py-4'>
                      <div className='flex items-start justify-between gap-3 mb-2'>
                        <p className='font-semibold text-gray-900 leading-snug'>
                          {r.rifa?.nombre || '—'}
                        </p>
                        <span
                          className='shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold'
                          style={{ backgroundColor: sc.bg, color: sc.fg }}
                        >
                          {r.estado}
                        </span>
                      </div>
                      <div className='flex items-center gap-2 text-[13px] text-gray-500 mb-3'>
                        <Calendar size={13} />
                        <span>{r.fechaReserva ? moment(r.fechaReserva).format('LL') : '—'}</span>
                      </div>
                      <div className='flex items-center justify-between'>
                        <span
                          className='inline-block rounded-full px-3 py-0.5 text-xs font-medium'
                          style={{ backgroundColor: '#F5F5F5', color: '#555' }}
                        >
                          {ticketsCount} {ticketsCount === 1 ? 'número' : 'números'}
                        </span>
                        {r.importeReserva != null && (
                          <span
                            className='text-sm font-bold'
                            style={{ color: 'var(--secondaryColor, #333)' }}
                          >
                            ${r.importeReserva}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <>
            {/* ── Info card ─────────────────────────────────── */}
            <div className='rounded-2xl bg-white shadow-sm overflow-hidden'>
              {/* Card header */}
              <div className='flex items-center justify-between px-5 py-4 border-b border-gray-100'>
                <p className='text-sm font-semibold text-gray-800'>{t('profile.personalInfo')}</p>
                {!editing ? (
                  <button
                    onClick={() => setEditing(true)}
                    className='flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors hover:bg-gray-100'
                    style={{ color: 'var(--secondaryColor)' }}
                  >
                    <Pencil size={13} />
                    {t('common.edit')}
                  </button>
                ) : (
                  <button
                    onClick={cancelEdit}
                    className='flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors'
                  >
                    <X size={13} />
                    {t('common.cancel')}
                  </button>
                )}
              </div>

              {/* Card body */}
              {!editing ? (
                <div className='px-5 py-5 space-y-4'>
                  <ReadField label={t('auth.name')} value={userInfo.nombre} />
                  <ReadField label={t('auth.email')} value={userInfo.email} />
                  <ReadField label={t('auth.telefono', 'Teléfono')} value={userInfo.telefono} />
                </div>
              ) : (
                <form onSubmit={saveInfo} className='px-5 py-5 space-y-4'>
                  <EditInput
                    label={t('auth.name')}
                    value={nombre}
                    onChange={e => setNombre(e.target.value)}
                    autoComplete='name'
                  />
                  <EditInput
                    label={t('auth.email')}
                    value={email}
                    onChange={e => setEmail(e.target.value.toLowerCase())}
                    type='email'
                    autoComplete='email'
                  />
                  <EditInput
                    label={t('auth.telefono', 'Teléfono')}
                    value={telefono}
                    onChange={e => setTelefono(e.target.value)}
                    type='tel'
                    autoComplete='tel'
                  />
                  <div className='flex gap-3 pt-1'>
                    <button
                      type='submit'
                      disabled={savingInfo}
                      className='flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50'
                      style={{
                        backgroundColor: 'var(--secondaryColor)',
                        border: 'none',
                        cursor: savingInfo ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Check size={15} />
                      {savingInfo ? t('common.loading') : t('common.save')}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* ── Change password card ───────────────────────── */}
            <div className='rounded-2xl bg-white shadow-sm overflow-hidden'>
              {/* Accordion trigger */}
              <button
                type='button'
                onClick={() => setPwOpen(v => !v)}
                className='flex w-full items-center justify-between px-5 py-4 text-left'
              >
                <p className='text-sm font-semibold text-gray-800'>{t('auth.changePassword')}</p>
                {pwOpen ? (
                  <ChevronUp size={16} className='text-gray-400' />
                ) : (
                  <ChevronDown size={16} className='text-gray-400' />
                )}
              </button>

              {/* Accordion body */}
              {pwOpen && (
                <form
                  onSubmit={savePassword}
                  className='px-5 pb-5 space-y-4 border-t border-gray-100 pt-4'
                >
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#7C7C7C',
                        marginBottom: '6px',
                      }}
                    >
                      {t('profile.currentPassword')}
                    </label>
                    <EyeInput
                      value={oldPass}
                      onChange={e => setOldPass(e.target.value)}
                      placeholder='••••••••'
                      autoComplete='current-password'
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#7C7C7C',
                        marginBottom: '6px',
                      }}
                    >
                      {t('profile.newPassword')}
                    </label>
                    <EyeInput
                      value={newPass}
                      onChange={e => setNewPass(e.target.value)}
                      placeholder='••••••••'
                      autoComplete='new-password'
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#7C7C7C',
                        marginBottom: '6px',
                      }}
                    >
                      {t('profile.confirmNewPassword')}
                    </label>
                    <EyeInput
                      value={confirmPw}
                      onChange={e => setConfirmPw(e.target.value)}
                      placeholder='••••••••'
                      autoComplete='new-password'
                    />
                  </div>

                  {confirmPw && newPass !== confirmPw && (
                    <p style={{ fontSize: '11px', color: '#d32f2f' }}>
                      {t('auth.passwordsDontMatch')}
                    </p>
                  )}

                  <button
                    type='submit'
                    disabled={savingPw || !oldPass || !newPass || newPass !== confirmPw}
                    className='w-full rounded-xl py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50'
                    style={{
                      backgroundColor: 'var(--secondaryColor)',
                      border: 'none',
                      cursor:
                        savingPw || !oldPass || !newPass || newPass !== confirmPw
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                  >
                    {savingPw ? t('common.loading') : t('auth.changePassword')}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
